import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useAlert } from '../../context/AlertContext';
import {
  IPostcode,
  IPostcodeReferences,
  useDeletePostcodes,
  useMigratePostcodes,
  usePostcodeList,
  usePostcodeReferences,
} from '../../hooks/reactQuerys/PostcodeAdminQueries';

interface IProps {
  open: boolean;
  mode: 'migrate' | 'delete';
  sources: IPostcode[];
  onClose: () => void;
  onDone: () => void;
}

const backendError = (err: unknown): string | undefined =>
  (err as { body?: { error?: string } })?.body?.error;

const label = (p: IPostcode) => `${p.Postcode} ${p.City}`.trim();

/**
 * Migration bzw. Löschung von Postleitzahlen. In beiden Fällen wird eine
 * Ziel-PLZ gewählt, die betroffenen Verknüpfungen werden angezeigt und
 * anschließend transaktional umgestellt (beim Löschen zusätzlich die Quell-PLZ
 * entfernt).
 */
export const PostcodeMigrateDialog = ({
  open,
  mode,
  sources,
  onClose,
  onDone,
}: IProps) => {
  const { showAlert } = useAlert();
  const sourceIds = useMemo(() => sources.map(s => s.idPostcode), [sources]);

  const [targetSearch, setTargetSearch] = useState('');
  const [target, setTarget] = useState<IPostcode | null>(null);
  const [refs, setRefs] = useState<IPostcodeReferences | null>(null);

  const { postcodeResult, isFetchingPostcodes } = usePostcodeList(
    targetSearch,
    0,
    20
  );
  const { loadReferences, isLoadingReferences } = usePostcodeReferences();
  const { migratePostcodes, isMigratingPostcodes } = useMigratePostcodes();
  const { deletePostcodes, isDeletingPostcodes } = useDeletePostcodes();

  useEffect(() => {
    if (open) {
      setTarget(null);
      setTargetSearch('');
      setRefs(null);
      if (sourceIds.length) {
        loadReferences(sourceIds)
          .then(setRefs)
          .catch(() => setRefs(null));
      }
    }
  }, [open, sourceIds.join(',')]);

  /*
   * Die markierten Postleitzahlen bleiben in der Liste, werden aber gesperrt.
   * Vorher wurden sie herausgefiltert — wer genau die markierte Postleitzahl als
   * Ziel suchte, sah dann nur „Keine Optionen" und erfuhr nicht, warum. Das
   * Backend weist Quelle gleich Ziel ohnehin ab; hier fehlte allein die
   * Erklaerung (QS #17, Testfall 10).
   */
  const options = postcodeResult?.rows ?? [];

  const busy = isMigratingPostcodes || isDeletingPostcodes;

  const handleConfirm = async () => {
    if (!target) {
      showAlert({
        text: 'Bitte Ziel-Postleitzahl wählen.',
        severity: 'warning',
      });
      return;
    }
    try {
      if (mode === 'delete') {
        const res = (await deletePostcodes({
          sourceIds,
          targetId: target.idPostcode,
        })) as { deleted?: number; migrated?: number };
        showAlert({
          text: `${res.deleted ?? 0} Postleitzahl(en) gelöscht, ${
            res.migrated ?? 0
          } Verknüpfung(en) migriert.`,
          severity: 'success',
        });
      } else {
        const res = (await migratePostcodes({
          sourceIds,
          targetId: target.idPostcode,
        })) as { migrated?: number };
        showAlert({
          text: `${res.migrated ?? 0} Verknüpfung(en) auf ${label(
            target
          )} migriert.`,
          severity: 'success',
        });
      }
      onDone();
      onClose();
    } catch (err) {
      showAlert({
        text: backendError(err) ?? 'Aktion fehlgeschlagen.',
        severity: 'error',
      });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle>
        {mode === 'delete'
          ? 'Postleitzahl(en) löschen'
          : 'Postleitzahl(en) migrieren'}
      </DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          {mode === 'delete'
            ? 'Vor dem Löschen werden alle Verknüpfungen der ausgewählten Postleitzahlen auf eine Ziel-Postleitzahl migriert. Anschließend werden die ausgewählten Einträge gelöscht.'
            : 'Alle Verknüpfungen der ausgewählten Postleitzahlen werden auf die gewählte Ziel-Postleitzahl umgestellt.'}
        </DialogContentText>

        <Typography variant='subtitle2'>Ausgewählte Postleitzahlen</Typography>
        <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
          {sources.map(label).join(', ')}
        </Typography>

        <Box sx={{ mb: 2 }}>
          <Typography variant='subtitle2' gutterBottom>
            Betroffene Verknüpfungen
          </Typography>
          {isLoadingReferences ? (
            <CircularProgress size={20} />
          ) : refs ? (
            refs.total === 0 ? (
              <Alert severity='info'>
                Keine verknüpften Datensätze — nichts zu migrieren.
              </Alert>
            ) : (
              <>
                <Alert severity='warning' sx={{ mb: 1 }}>
                  Insgesamt <strong>{refs.total}</strong> Datensatz/Datensätze
                  betroffen.
                </Alert>
                <List dense disablePadding>
                  {refs.byTable.map(r => (
                    <ListItem key={`${r.table}.${r.column}`} disablePadding>
                      <ListItemText primary={`${r.table}: ${r.count}`} />
                    </ListItem>
                  ))}
                </List>
              </>
            )
          ) : null}
        </Box>

        <Autocomplete
          options={options}
          value={target}
          onChange={(_, value) => setTarget(value)}
          onInputChange={(_, value) => setTargetSearch(value)}
          getOptionLabel={label}
          isOptionEqualToValue={(o, v) => o.idPostcode === v.idPostcode}
          getOptionDisabled={o => sourceIds.includes(o.idPostcode)}
          renderOption={(props, option) => {
            const isSource = sourceIds.includes(option.idPostcode);

            return (
              <ListItem {...props} key={option.idPostcode}>
                <ListItemText
                  primary={label(option)}
                  secondary={
                    isSource
                      ? 'bereits ausgewählt — als Ziel nicht möglich'
                      : undefined
                  }
                />
              </ListItem>
            );
          }}
          noOptionsText='Keine Postleitzahl gefunden.'
          loading={isFetchingPostcodes}
          filterOptions={x => x}
          renderInput={params => (
            <TextField
              {...params}
              label='Ziel-Postleitzahl'
              placeholder='Postleitzahl oder Ort suchen'
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {isFetchingPostcodes ? (
                      <CircularProgress color='inherit' size={18} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Abbrechen</Button>
        <Button
          variant='contained'
          color={mode === 'delete' ? 'error' : 'primary'}
          onClick={handleConfirm}
          disabled={busy || !target}
        >
          {busy ? (
            <CircularProgress size={20} />
          ) : mode === 'delete' ? (
            'Migrieren & löschen'
          ) : (
            'Migrieren'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
